import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TranslatePipe } from "@ngx-translate/core";
import { of } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { MarknadsprislistaTyp } from "~app/common/models/constants";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { Volatile } from "~app/common/services/statedata.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { MarknadsprislistaSearchDTO } from "../../../../common/models/virkeskop/v2/MarknadsprislistaSearchDTO";
import { IHandelssortimentsgrupp, IMarknadsprislista, IMarknadsprislistaSearchParamsModel } from "../../models/marknadsprislista-model";
import { IMarknadsomradeMultiSelector } from "../../models/verksamhetsomraden-models";
import { MarknadsprislistaService } from "../../services/marknadsprislista.service";

@Component({
    selector: "rask-marknadsprislista-search",
    templateUrl: "./marknadsprislista-search.component.html",
    styleUrls: ["./marknadsprislista-search.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        MultiSelectorComponent,
        DatepickerComponent,
        CheckboxComponent,
        RadiobuttonGroupComponent,
        SubmitButtonComponent,
        TranslatePipe
    ]
})
export class MarknadsprislistaSearchComponent implements OnInit {

    activetypes: IRadiobuttonItem[] = [
        { key: MarknadsprislistaTyp.Viol2,
            value: MarknadsprislistaTyp.AllaWithKeys[MarknadsprislistaTyp.Viol2] },
        { key: MarknadsprislistaTyp.Viol3,
            value: MarknadsprislistaTyp.AllaWithKeys[MarknadsprislistaTyp.Viol3] },
        { key: MarknadsprislistaTyp.Skordarmatning,
            value: MarknadsprislistaTyp.AllaWithKeys[MarknadsprislistaTyp.Skordarmatning] },
        {
            key: "",
            value: "Alla"
        }
    ];

    @Input() handelssortimentGrupper: IHandelssortimentsgrupp[];
    @Input() marknadsomrade: IMarknadsomradeMultiSelector[];

    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output("marknadsprislistor") marknadsprislistor = new EventEmitter<IMarknadsprislista[]>(true);
    searchMarknadsprislistaForm: UntypedFormGroup;
    searchParams: IMarknadsprislistaSearchParamsModel;
    volatile: Volatile;

    private readonly busyService = inject(BusyService);
    private readonly formBinder = inject(FormBinderService);
    private readonly marknadsprislistaService = inject(MarknadsprislistaService);
    private readonly webinstallningarService = inject(WebinstallningarService);
    private readonly wik = "Marknadsprislista.searchparams";

    getHandelssortimentsgrupper() {
        if (this.handelssortimentGrupper != null) {
            return of(this.handelssortimentGrupper);
        }
        return null;
    }

    getMarknadsomrade() {
        if (this.marknadsomrade != null) {
            return of(this.marknadsomrade);
        }
        return null;
    }

    ngOnInit() {
        this.searchParams = {
            benamning: "",
            violId: "",
            gallerFrom: null,
            gallerTom: null,
            typ: "",
            resultatomraden: [],
            handelssortimentsgrupper: [],
            isAktuell: null
        };

        this.webinstallningarService.applyTo<IMarknadsprislistaSearchParamsModel>(this.wik, this.searchParams);

        this.searchMarknadsprislistaForm = this.formBinder.CreateGroup(this.searchParams);
        this.search();
    }

    search() {
        this.mapFromFormToSearchModel();
        this.webinstallningarService.set<IMarknadsprislistaSearchParamsModel>(this.wik, this.searchParams);

        const searchObj: MarknadsprislistaSearchDTO = {
            benamning: this.searchParams.benamning,
            gallerFrom: this.searchParams.gallerFrom?.toString(),
            gallerTom: this.searchParams.gallerTom?.toString(),
            violId: this.searchParams.violId,
            typ: this.searchParams.typ,
            isAktuell: this.searchParams.isAktuell,
            resultatomraden: this.searchParams.resultatomraden.map(x => x.id),
            handelssortimentsgrupper: this.searchParams.handelssortimentsgrupper.map(x => x.id)
        };
        const query = this.marknadsprislistaService.searchMarknadsprislistor(searchObj);

        this.busyService.addWork(query).subscribe((res: IMarknadsprislista[]) => {
            this.marknadsprislistor.emit(res);
        });
    }

    private mapFromFormToSearchModel() {
        this.searchParams.benamning = this.searchMarknadsprislistaForm.get("benamning").value;
        this.searchParams.gallerFrom = this.searchMarknadsprislistaForm.get("gallerFrom")?.value ?? null;
        this.searchParams.gallerTom = this.searchMarknadsprislistaForm.get("gallerTom")?.value ?? null;
        this.searchParams.handelssortimentsgrupper = this.searchMarknadsprislistaForm.get("handelssortimentsgrupper").value;
        this.searchParams.resultatomraden = this.searchMarknadsprislistaForm.get("resultatomraden").value;
        this.searchParams.typ = this.searchMarknadsprislistaForm.get("typ")?.value ?? "";
        this.searchParams.violId = this.searchMarknadsprislistaForm.get("violId").value;
        this.searchParams.isAktuell = this.searchMarknadsprislistaForm.get("isAktuell")?.value ?? null;
    }

    private parseFalsyString(val) {
        if (val == null || val === "") {
            return null;
        }
        return val === "true" ? true : false;
    }
}
